import React, { useEffect, useRef, useState } from "react";
import { Stage } from "react-konva";
import { Layer } from "react-konva/lib/ReactKonvaCore";

import { getFocusCanvasList, setCanvasOneLine, setDeleteOneLine, setFocusShareDocument } from "../../../share-document";
import { translate } from "../../i18n";
import { getLocalParticipant, ROLE } from "../../participants";
import { connect } from "../../redux";
import { setCanvasProperty } from "../action";
import { CANVAS_TOOL } from "../constants";
import AllLineCanvas from "./AllLineCanvas";
import ScrollCanvas from "./ScrollCanvas";

const penChangeToolList = [CANVAS_TOOL.thickness, CANVAS_TOOL.opacity, CANVAS_TOOL.color,  CANVAS_TOOL.zoom];
const penList = [CANVAS_TOOL.pen, CANVAS_TOOL.eraser];
function CanvasDraw({
    t, dispatch, refresh, tempTextRef, canvasStyle, scale, isWhiteBoard, allLine, canvasX, canvasY, number,
    elementWidth, elementHeight, focusUuid, focusIndex, canvasTool, localParticipant,
    setCanvasX, setCanvasY
}) {
    const canvasRef = useRef();
    
    const [tool, setTool] = useState(CANVAS_TOOL.tool);

    const [down, setDown] = useState(false);
    const [drawId, setDrawId] = useState(null);
    const [line, setLine] = useState(null);
    const [scrollPos, setScrollPos] = useState(null);

    const mouseup = e => {
        if (e.target.tagName === 'CANVAS') return;


        if (drawId) dispatch(setCanvasOneLine(new Map([[drawId, line]]), focusUuid, String(focusIndex)));
        setDrawId(null);
        setDown(false);
    };

    useEffect(() => {
        setDrawId(null);
        setLine(null);
    }, [isWhiteBoard, focusUuid, focusIndex, refresh]);

    useEffect(() => {
        window.addEventListener("mouseup", mouseup, false);

        return () => {
            window.removeEventListener("mouseup", mouseup, false);
        }
    }, [mouseup]);   

    const onMouseDown = e => {
        e.preventDefault();
        dispatch(setCanvasProperty({ showOption: false }));
        let currentTool = tool;
        if (currentTool === CANVAS_TOOL.choice) return;
        if (currentTool === CANVAS_TOOL.handler) {
            const { x, y } = canvasRef.current.getRelativePointerPosition();
            setScrollPos({ type: "down", pos: {x, y} });
            
            return;
        }
        if (penChangeToolList.indexOf(currentTool) !== -1) currentTool = CANVAS_TOOL.pen;
        if (tool === CANVAS_TOOL.eraser && canvasTool.isPartEraser === false) {
            setDown(true);
            return;
        }

        const key = `${Date.now()}_${localParticipant.user_uuid}`;
        setDrawId(key);

        const { x, y } = getPointerPosition(e);

        const property = {
            canvasTool: {
                ...canvasStyle,
                tool: currentTool,
                thickness: canvasTool.thickness * (currentTool === CANVAS_TOOL.text ? 8 : 1),
                color: canvasTool.color,
                opacity: Number(canvasTool.opacity),
                figure: canvasTool.tool === CANVAS_TOOL.figure ? canvasTool.figure : null,
                rotation: 0
            },
            points: [ x, y ]
        };

        setLine(property); 
        
        if (penList.indexOf(currentTool) !== -1) {
            APP.mateManagement.sendPointer(key, property, isWhiteBoard, focusUuid, focusIndex);
        }
    }

    const onMouseMove = e => {
        if (!drawId) return;
        if (tool === CANVAS_TOOL.choice || tool === CANVAS_TOOL.handler) return;

        const { x, y } = getPointerPosition(e);
        if (penList.indexOf(canvasTool.tool) !== -1) {
            const newLine = {
                ...line,
                points: line.points.concat([x, y])
            };

            setLine(newLine); 

            APP.mateManagement.sendPointer(drawId, { points: [ x, y ] }, isWhiteBoard, focusUuid, focusIndex);
        } else if (canvasTool.tool === CANVAS_TOOL.figure) {
            const newLine = {
                ...line,
                canvasTool: {
                    ...line.canvasTool,
                    figureWidth: x - line.points[0], 
                    figureHeight: y - line.points[1]
                }
            };

            setLine(newLine);
        }
    }

    const onMouseUp = e => {
        e.preventDefault();
        e.stopPropagation();
        if (tool === CANVAS_TOOL.choice) return;
        if (tool === CANVAS_TOOL.handler) {
            const { x, y } = canvasRef.current.getRelativePointerPosition();
            setScrollPos({ type: "up", pos: { x, y } });
            
            return;
        }
        if (!drawId) return;

        let newLine;
        const { x, y } = getPointerPosition(e);
        if (penList.indexOf(canvasTool.tool) !== -1) {
            newLine = { ...line, points: line.points.concat([x, y]) };
            setLine(newLine); 

            APP.mateManagement.sendPointer(drawId, { points: [ x, y ] }, isWhiteBoard, focusUuid, focusIndex);            
        } else if (canvasTool.tool === CANVAS_TOOL.text) {
            newLine = { 
                ...line, 
                canvasTool: {
                    ...line.canvasTool,
                    figureWidth: 200 / window.devicePixelRatio,
                    figureHeight: 200 / window.devicePixelRatio
                },
                text: ""
            }
            setLine(newLine); 
            
            APP.mateManagement.sendPointer(drawId, newLine, isWhiteBoard, focusUuid, focusIndex);
            dispatch(setCanvasProperty({ showOption: false }));
        } else if (canvasTool.tool === CANVAS_TOOL.figure) {
            newLine = {
                ...line,
                canvasTool: {
                    ...line.canvasTool,
                    figureWidth: x - line.points[0], 
                    figureHeight: y - line.points[1]
                }
            };

            setLine(newLine);

            APP.mateManagement.sendPointer(drawId, newLine, isWhiteBoard, focusUuid, focusIndex);
        }

        dispatch(setCanvasOneLine(new Map([[drawId, newLine]]), focusUuid, String(focusIndex)));
        setDrawId(null);
        setDown(false);
    }

    const onWheel = e => {
        const dx = e.deltaX;
        const dy = e.deltaY;

        setScrollPos({ type: "wheel", pos: { x: dx, y: dy }});
    }

    const onKeyup = e => {
        e.preventDefault();
        switch (e.key) {
            case "ArrowLeft":
            case "ArrowDown":
                if (focusIndex - 1 > 0) handlerFocusItem(focusIndex - 1);
                break;
            case "ArrowRight":
            case "ArrowUp":
                if (focusIndex + 1 < number) handlerFocusItem(focusIndex + 1);
                break;
        }
    }

    function getPointerPosition(e) {
        const pos = canvasRef.current.getRelativePointerPosition();
        const x = (pos.x - canvasX) / scale;
        const y = (pos.y - canvasY) / scale;

        return { x, y };
    }

    // useEffect(throttle(() => {
    //     if (drawId && line) {
    //        
    //     }
    // }, 1000), [line]);

    useEffect(() => {        
        if (!drawId) setLine(null);
    }, [drawId]);

    useEffect(() => {
        setTool(canvasTool.tool);
    }, [canvasTool]);

    const handlerSelectedLine = (uuid) => {
        if (permissionDraw(uuid)) return;
        setLine(null);
        setDrawId(uuid);
        
        const line = allLine.find(value => value[0] === uuid);
        if (line) setLine(line[1]);
    }

    const handlerDeleteLine = deleteLine => {
        if (!down) return;

        if (canvasTool.tool === CANVAS_TOOL.eraser && canvasTool.isPartEraser === false) {
            if (permissionDraw(deleteLine)) return;

            dispatch(setDeleteOneLine(deleteLine, focusUuid, focusIndex));            
            APP.mateManagement.deletePointer(deleteLine, false, isWhiteBoard, focusUuid, focusIndex);
        }        
    }

    const handlerFocusItem = index => {
        if (!isWhiteBoard) dispatch(setFocusShareDocument(focusUuid, index, isWhiteBoard, false));
    }

    const permissionDraw = (uuid) => {
        return localParticipant.role === ROLE.PARTICIPANT && uuid.indexOf(localParticipant.user_uuid) === -1 ? true : false;
    }

    useEventListener("touchstart", onMouseDown, canvasRef.current);
    useEventListener("touchmove", onMouseMove, canvasRef.current);
    useEventListener("touchend touchcancel", onMouseUp, canvasRef.current);

    useEventListener("wheel", onWheel, canvasRef.current);

    useEventListener("mousedown", onMouseDown, canvasRef.current);
    useEventListener("mousemove", onMouseMove, canvasRef.current);
    useEventListener("mouseup", onMouseUp, canvasRef.current);

    return (
        <Stage ref={canvasRef} className="canvas_container" width={Math.min(canvasStyle.width * scale, elementWidth)} height={Math.min(canvasStyle.height * scale, elementHeight)}>  
            <Layer scaleX={scale} scaleY={scale} x={canvasX} y={canvasY}>
                { allLine && allLine.map(([uuid, value], key) => {
                    if (uuid === drawId) return null;
                    return (
                        <React.Fragment key={key}>
                            <AllLineCanvas t={t} tempTextRef={tempTextRef}
                                selected={false}
                                line={value} 
                                uuid={uuid}
                                drawId={drawId} 
                                scale={scale}
                                canvasX={canvasX}
                                canvasY={canvasY}
                                currentTool={canvasTool}
                                isWhiteBoard={isWhiteBoard}
                                canvasStyle={canvasStyle}
                                setLine={setLine}
                                handlerSelectedLine={handlerSelectedLine}
                                handlerDeleteLine={handlerDeleteLine}
                            />
                        </React.Fragment>
                    )
                })}  

                { drawId && line && <AllLineCanvas t={t} tempTextRef={tempTextRef}
                    selected={true}
                    line={line} 
                    uuid={drawId}
                    drawId={drawId} 
                    scale={scale}
                    canvasX={canvasX}
                    canvasY={canvasY}
                    currentTool={canvasTool}
                    isWhiteBoard={isWhiteBoard}
                    canvasStyle={canvasStyle}
                    setLine={setLine}
                    handlerSelectedLine={handlerSelectedLine}
                    handlerDeleteLine={handlerDeleteLine}
                /> }
            </Layer>
            <Layer>
                <ScrollCanvas 
                    canvasRef={canvasRef}
                    elementWidth={elementWidth}
                    elementHeight={elementHeight}
                    canvasStyle={canvasStyle} 
                    scale={scale}
                    scrollPosition={scrollPos}
                    canvasX={canvasX}
                    canvasY={canvasY}
                    focusIndex={focusIndex}
                    number={number}

                    setCanvasX={setCanvasX}
                    setCanvasY={setCanvasY}
                    handlerFocusItem={handlerFocusItem}
                />                
            </Layer>            
        </Stage>
    )
}

function _mapStateToProps(state, ownProps) {
    const canvasTool = state['features/base/canvas'];
    const localParticipant = getLocalParticipant(state);
    const allLine = getFocusCanvasList(state, ownProps.focusUuid, ownProps.focusIndex);

    return {
        canvasTool,
        localParticipant,
        allLine: allLine ? [...allLine] : null
    }
}

export default translate(connect(_mapStateToProps)(CanvasDraw));

function useEventListener(eventName, handler, element = window) {    
    const savedHandler = useRef();

    useEffect(() => {
      savedHandler.current = handler;
    }, [handler]);

    useEffect(() => {
        const isSupported = element && element.addEventListener;
        if (!isSupported) return;
        
        const eventListener = (event) => savedHandler.current(event);
        element.addEventListener(eventName, eventListener, isPassive() ? { capture: false, passive: false } : false);
        
        return () => {
            element.removeEventListener(eventName, eventListener, isPassive() ? { capture: false, passive: false } : false);
            
        };
    }, [eventName, element]);
}

function isPassive() {
    var supportsPassiveOption = false;
    try {
        addEventListener("test", null,
            Object.defineProperty({}, "passive", {
                get: () => { supportsPassiveOption = true; }
            })
        );
    } catch (e) {}
    return supportsPassiveOption;
}